import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell, AuthField } from "@/components/AuthShell";
import { BArecordMark } from "@/components/BArecordMark";
import { useSafeMeAuth, formatApiError } from "@/context/SafeMeAuthContext";

function SafeMeBrand() {
  return (
    <div className="flex items-center gap-2.5" data-testid="safeme-brand">
      <BArecordMark size={20} className="text-paper" />
      <span className="font-display text-lg tracking-tight text-paper">BArecord</span>
      <span className="text-[9px] uppercase tracking-widest bg-white/10 text-paper rounded-full px-1.5 py-0.5">by Aliwer</span>
    </div>
  );
}

export default function SafeMeLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useSafeMeAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/barecord", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/barecord");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      brand={<SafeMeBrand />}
      tagline="BArecord — Business Archive Record: capture, secure & understand every conversation."
      panelHeadline="Business. Captured. Scored. Governed."
      title="Sign in to BArecord"
      subtitle="Secure communications intelligence, powered by AI. Sign in with your BArecord account to continue."
      image="https://static.prod-images.emergentagent.com/jobs/a7857c63-2010-4de3-a354-ef80fc69c639/images/29c5a35a6953ad6be50a2bfca7e8fe84874ff7ea36a30de0c488e32f298c2cca.png"
      foot={
        <>
          Don't have an account?{" "}
          <Link to="/barecord/register" className="text-paper underline underline-offset-4" data-testid="safeme-to-register">
            Create one
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="safeme-login-form">
        <AuthField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required testid="safeme-login-email" />
        <AuthField label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required testid="safeme-login-password" />
        {error && <p className="text-sm text-red-400" data-testid="safeme-login-error">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          data-testid="safeme-login-submit"
          className="w-full rounded-full bg-paper text-black text-sm font-medium py-3.5 hover:bg-white transition-colors disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Enter BArecord"}
        </button>
      </form>
    </AuthShell>
  );
}
